const NAME_MIN_LENGTH = 5;
const NAME_MAX_LENGTH = 100;
const EMAIL_MAX_LENGTH = 254;
const EMAIL_LOCAL_MAX_LENGTH = 64;
const MESSAGE_MAX_LENGTH = 1000;

const NAME_ALLOWED_CHARS_REGEX = /^[\p{L}'’ ]+$/u;
const EMAIL_LOCAL_ALLOWED_CHARS_REGEX = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
const EMAIL_DOMAIN_LABEL_REGEX = /^[A-Za-z0-9-]+$/;
const EMAIL_TLD_REGEX = /^[A-Za-z]{2,24}$/;
const EMOJI_REGEX = /\p{Extended_Pictographic}/u;
const MESSAGE_HAS_LETTER_OR_NUMBER_REGEX = /[\p{L}\p{N}]/u;

const MESSAGE_INJECTION_PATTERNS = [
  /<\s*script\b/i,
  /<\s*img\b[^>]*\bsrc\s*=/i,
  /on\w+\s*=/i,
  /<\s*\/?\s*[a-z][^>]*>/i,
  /(?:^|[\r\n])\s*(bcc|cc|content-type|mime-version|to|from|subject)\s*:/i,
  /\{\{\s*"mensaje"\s*:\s*\}\}/i,
  /\{\{\s*".+"\s*:\s*\}\}/i,
];

const isEmpty = (value) => !value || !value.trim();

export const validateContactName = (value) => {
  const normalizedValue = value?.trim() || "";

  if (isEmpty(normalizedValue)) {
    return "El campo es inválido";
  }

  if (normalizedValue.length < NAME_MIN_LENGTH) {
    return `El nombre debe tener al menos ${NAME_MIN_LENGTH} caracteres.`;
  }

  if (normalizedValue.length > NAME_MAX_LENGTH) {
    return `El nombre no puede superar los ${NAME_MAX_LENGTH} caracteres.`;
  }

  if (!NAME_ALLOWED_CHARS_REGEX.test(normalizedValue)) {
    return "El nombre solo puede contener letras, espacios y apóstrofes.";
  }

  return "";
};

export const validateContactEmail = (value) => {
  const normalizedValue = value?.trim() || "";

  if (isEmpty(normalizedValue)) {
    return "Completá este campo.";
  }

  if (normalizedValue.length > EMAIL_MAX_LENGTH) {
    return `El correo no puede superar los ${EMAIL_MAX_LENGTH} caracteres.`;
  }

  if (/\s/.test(normalizedValue)) {
    return "El correo no puede contener espacios.";
  }

  const atCount = (normalizedValue.match(/@/g) || []).length;
  if (atCount !== 1) {
    return "Ingresá un correo electrónico válido.";
  }

  const [localPart, domainPart] = normalizedValue.split("@");

  if (!localPart || !domainPart) {
    return "Ingresá un correo electrónico válido.";
  }

  if (localPart.length > EMAIL_LOCAL_MAX_LENGTH) {
    return `La parte local del correo no puede superar los ${EMAIL_LOCAL_MAX_LENGTH} caracteres.`;
  }

  if (
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..")
  ) {
    return "Ingresá un correo electrónico válido.";
  }

  if (!EMAIL_LOCAL_ALLOWED_CHARS_REGEX.test(localPart)) {
    return "Ingresá un correo electrónico válido.";
  }

  if (
    domainPart.startsWith(".") ||
    domainPart.endsWith(".") ||
    domainPart.includes("..")
  ) {
    return "Ingresá un correo electrónico válido.";
  }

  const domainLabels = domainPart.split(".");
  if (domainLabels.length < 2) {
    return "Ingresá un correo electrónico válido.";
  }

  const tld = domainLabels[domainLabels.length - 1];
  if (!EMAIL_TLD_REGEX.test(tld)) {
    return "Ingresá un correo electrónico válido.";
  }

  const invalidDomainLabel = domainLabels.some(
    (label) =>
      !label ||
      label.startsWith("-") ||
      label.endsWith("-") ||
      !EMAIL_DOMAIN_LABEL_REGEX.test(label),
  );

  if (invalidDomainLabel) {
    return "Ingresá un correo electrónico válido.";
  }

  return "";
};

export const validateContactMessage = (value) => {
  const normalizedValue = value?.trim() || "";

  if (isEmpty(normalizedValue)) {
    return "Escribí un mensaje antes de enviar";
  }

  if (normalizedValue.length > MESSAGE_MAX_LENGTH) {
    return `El mensaje no puede superar los ${MESSAGE_MAX_LENGTH} caracteres`;
  }

  if (!MESSAGE_HAS_LETTER_OR_NUMBER_REGEX.test(normalizedValue)) {
    return "El mensaje no puede contener solo símbolos o emojis";
  }

  if (EMOJI_REGEX.test(normalizedValue)) {
    return "El mensaje no puede contener emojis";
  }

  const hasSuspiciousContent = MESSAGE_INJECTION_PATTERNS.some((pattern) =>
    pattern.test(normalizedValue),
  );
  if (hasSuspiciousContent) {
    return "El mensaje contiene contenido no permitido";
  }

  return "";
};

export const validateContactForm = ({ name, email, message }) => ({
  name: validateContactName(name),
  email: validateContactEmail(email),
  message: validateContactMessage(message),
});
